import { useRef, useState, type ReactNode } from 'react';
import { Button } from '@indipia/ui-core';
import { OnboardingTour, type OnboardingTourHandle, type TourStep } from '@indipia/agents-scheduler/src/components/OnboardingTour';
import { ThumbImg } from '@indipia/agents-scheduler/src/components/ThumbImg';
import { ErrorBanner } from '@indipia/agents-scheduler/src/components/AsyncState';
import { PartnerCandidateItem } from '@indipia/agents-scheduler/src/components/PartnerCandidateItem';
import {
  PartnerCard,
  type PartnerCardPartner,
  type PartnerCardTask,
  type PartnerSelection,
} from '@indipia/agents-scheduler/src/components/PartnerCard';
import { InfoBanner } from '@indipia/agents-scheduler/src/components/InfoBanner';
import { ForemanChangeNotice } from '@indipia/agents-scheduler/src/components/ForemanChangeNotice';
import { ScheduleChangeNoticeModal } from '@indipia/agents-scheduler/src/components/ScheduleChangeNoticeModal';
import type { DateChange, PartnerCandidate } from '@indipia/agents-scheduler/src/api/foremanApi';
import { ShipStatusPopup } from '@indipia/agents-scheduler/src/components/ShipStatusPopup';
import { TodayTodoNotiSettingRow } from '@indipia/agents-scheduler/src/components/TodayTodoNotiSettingRow';

// ── OnboardingTour ──
const TOUR_STEPS: TourStep[] = [
  { selector: null, title: '공정표에 오신 걸 환영해요', body: '주요 기능을 짧게 안내해 드릴게요.' },
  { selector: '#aglc-tour-a', title: '공종 추가', body: '여기서 새 공종을 추가해요.' },
  { selector: '#aglc-tour-b', title: '일정확인 발송', body: '배정된 반장님께 일정확인 카톡을 보내요.' },
];

function OnboardingTourDemo() {
  const ref = useRef<OnboardingTourHandle>(null);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <span id="aglc-tour-a" className="inline-flex"><Button size="sm" variant="secondary">공종 추가</Button></span>
        <span id="aglc-tour-b" className="inline-flex"><Button size="sm" variant="secondary">일정확인 보내기</Button></span>
      </div>
      <div><Button size="sm" onClick={() => ref.current?.start()}>투어 시작</Button></div>
      <OnboardingTour ref={ref} storageKey="agent-gallery-tour" steps={TOUR_STEPS} autoStart={false} />
    </div>
  );
}

// ── PartnerCandidateItem ──
const BASE_CANDIDATE: PartnerCandidate = {
  partnerId: 101,
  partnerName: '김도배',
  mobile: '010-1234-5678',
  partnerType: 'INDIVIDUAL',
  company: false,
  workTypeMatch: 'MAIN',
  orderZero: false,
  avgRating: 4.8,
  reviewCount: 32,
  collabCount: 12,
  replyWithinTwoHoursPercent: 92,
  reportComplianceRate: null,
  defectReportRate: null,
  regions: [{ code: '11', name: '서울' }, { code: '41', name: '경기' }],
  regionMatch: true,
  dealSuspended: false,
  current: true,
  declined: false,
  declinedDtm: null,
  scheduleConflict: false,
  conflicts: null,
  alreadySent: true,
  sendCount: 1,
  lastSentDtm: '2026-10-14T09:30:00',
  sendFailed: false,
};

const CONFLICT_CANDIDATE: PartnerCandidate = {
  ...BASE_CANDIDATE,
  partnerId: 102,
  partnerName: '(주)바른타일',
  mobile: '010-9876-5432',
  partnerType: 'COMPANY',
  company: true,
  workTypeMatch: 'SUB',
  avgRating: 4.2,
  reviewCount: 8,
  collabCount: 3,
  replyWithinTwoHoursPercent: 64,
  regions: [{ code: '41', name: '경기' }],
  regionMatch: false,
  current: false,
  scheduleConflict: true,
  conflicts: [
    { siteId: 501, siteName: '분당 정자동 아파트', startDate: '2026-10-20', endDate: '2026-10-22' },
    { siteId: 502, siteName: '수원 영통 빌라', startDate: '2026-10-24', endDate: '2026-10-25' },
  ],
  alreadySent: false,
  sendCount: 0,
  lastSentDtm: null,
};

function PartnerCandidateItemDemo() {
  const [selectedId, setSelectedId] = useState(BASE_CANDIDATE.partnerId);
  return (
    <div className="flex flex-col gap-2">
      {[BASE_CANDIDATE, CONFLICT_CANDIDATE].map(c => (
        <PartnerCandidateItem
          key={c.partnerId}
          candidate={c}
          selected={selectedId === c.partnerId}
          onSelect={x => setSelectedId(x.partnerId)}
        />
      ))}
    </div>
  );
}

// ── PartnerCard ── 확정 반장(101)과 다른 반장(102)을 고른 상태 → 변경 경고·사유까지 보인다.
// 후보 목록(Combobox)은 펼칠 때만 조회하므로 첫 화면은 API 를 부르지 않는다.
const PARTNER_TASK: PartnerCardTask = {
  taskId: 9001,
  partnerStatus: 'CONFIRMED',
  assignSource: 'AUTO',
  sentDtm: '2026-10-14T09:30:00',
  respondedDtm: '2026-10-14T11:05:00',
  assigneePartnerId: 101,
  foremanName: '김도배',
  foremanContact: '010-1234-5678',
  startDate: new Date(2026, 9, 20),
  endDate: new Date(2026, 9, 23),
};

const PARTNERS: PartnerCardPartner[] = [
  { partnerId: 101, partnerName: '김도배', mobile: '010-1234-5678' },
  { partnerId: 102, partnerName: '(주)바른타일', mobile: '010-9876-5432' },
];

function PartnerCardDemo() {
  const [sel, setSel] = useState<PartnerSelection>({ partnerId: 102, name: '(주)바른타일' });
  const [reason, setReason] = useState('');
  return (
    <div className="max-w-sm">
      <PartnerCard
        task={PARTNER_TASK}
        selectedPartnerId={sel.partnerId}
        selectedName={sel.name}
        partners={PARTNERS}
        onSelect={setSel}
        onSent={() => {}}
        changeReason={reason}
        onChangeReason={setReason}
        onChanged={() => {}}
        signal={{
          state: 'ok',
          taskCtx: { startDate: '2026-10-20', endDate: '2026-10-22', partnerName: '김도배' },
          item: {
            signalTypeCode: 'FOREMAN', colorCode: 'GREEN', reasonCode: 'FOREMAN_CONFIRMED',
            reasonDate: '2026-10-14', reasonDtm: '2026-10-14T11:05:00', todoCode: null, prevColorCode: 'YELLOW', nextJudgeDtm: null,
          },
        }}
      />
    </div>
  );
}

// ── ForemanChangeNotice ──
function ForemanChangeNoticeDemo() {
  const [reason, setReason] = useState('');
  return (
    <div className="max-w-sm">
      <ForemanChangeNotice previousName="김도배" reason={reason} onReasonChange={setReason} />
    </div>
  );
}

// ── ScheduleChangeNoticeModal ──
const DATE_CHANGES: DateChange[] = [
  {
    taskId: 9001, workTypeCode: 'WALLPAPER', workTypeName: '도배',
    oldStartDate: '2026-10-20', oldEndDate: '2026-10-21', newStartDate: '2026-10-22', newEndDate: '2026-10-23',
    partnerId: 101, partnerName: '김도배', partnerStatus: 'CONFIRMED', notifiable: true,
  },
  {
    taskId: 9002, workTypeCode: 'TILE', workTypeName: '타일',
    oldStartDate: '2026-10-23', oldEndDate: '2026-10-24', newStartDate: '2026-10-25', newEndDate: '2026-10-27',
    partnerId: 102, partnerName: '(주)바른타일', partnerStatus: 'WAITING', notifiable: true,
  },
];

function ScheduleChangeNoticeModalDemo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>열기</Button>
      {open && (
        <ScheduleChangeNoticeModal
          scheduleId={7001}
          changes={DATE_CHANGES}
          onSent={() => {}}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// 앱 공통 QueryProvider(AppShell) 아래에서 열리므로 별도 QueryClientProvider 없이 조회 훅이 동작한다.
// guides 화면은 API 차단이라 오류 상태, 앱 화면은 실제 조회 결과가 보인다.
const SHIP_DEMO_TASK_NAMES: ReadonlyMap<number, string> = new Map([[9001, '도배'], [9002, '타일']]);

function ShipStatusPopupDemo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>열기</Button>
      {open && (
        <ShipStatusPopup
          scheduleId={7001}
          siteName="강남 리모델링"
          taskNameById={SHIP_DEMO_TASK_NAMES}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export const demos: Record<string, () => ReactNode> = {
  ShipStatusPopup: () => <ShipStatusPopupDemo />,
  // 마운트 시 설정을 조회한다 — guides 화면은 API 차단이라 "다시 불러오기", 앱 화면은 실제 설정값이 보인다.
  TodayTodoNotiSettingRow: () => (
    <div className="flex flex-col gap-2">
      <TodayTodoNotiSettingRow />
      <p className="text-caption text-text-secondary">guides 화면: 조회 차단 → "다시 불러오기" 상태 · 앱 화면: 실제 설정값(변경 저장은 차단)</p>
    </div>
  ),
  OnboardingTour: () => <OnboardingTourDemo />,
  ThumbImg: () => (
    <div className="flex flex-col items-start gap-2">
      <ThumbImg url="/api/worklog/demo/thumb.jpg" alt="현장보고 사진" className="w-24 h-24 rounded" />
      <span className="text-sm text-muted">API 차단 상태 → fallback</span>
    </div>
  ),
  ErrorBanner: () => <ErrorBanner onRetry={() => {}} />,
  PartnerCandidateItem: () => <PartnerCandidateItemDemo />,
  PartnerCard: () => <PartnerCardDemo />,
  InfoBanner: () => <InfoBanner>공종 바를 끌어서 일정을 옮길 수 있어요. 바뀐 일정은 반장님께 알릴 수 있어요.</InfoBanner>,
  ForemanChangeNotice: () => <ForemanChangeNoticeDemo />,
  ScheduleChangeNoticeModal: () => <ScheduleChangeNoticeModalDemo />,
};
